/**
 * 批量任务 Store (Pinia) - 统一任务管理架构
 *
 * 职责：
 * - 文件管理：上传、解析、查询文件信息
 * - 任务管理：使用统一的 taskStore（来自 task.ts）
 *
 * 架构说明：
 * - 批量任务的任务状态、列表等由 taskStore 统一管理
 * - 本 store 只负责文件相关的业务逻辑
 * - 下载链接通过 assetId 从 libraryStore 获取
 *
 * @version 2.0.0 - 统一任务管理架构
 * @author Frontend Team
 * @since 2025-01-17
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { batchAPI } from '@/api/batch'
import type {
  BatchFile,
  ProcessedFile,
  BatchFileStatus
} from '@/types/batch'

/**
 * 归一化状态结构
 */
interface NormalizedState<T> {
  byUid: Record<string, T>
  allUids: string[]
}

export const useBatchStore = defineStore('batch', () => {
  // ============= 状态定义 =============

  // 文件存储（归一化）
  const files = ref<NormalizedState<BatchFile>>({
    byUid: {},
    allUids: []
  })

  // 文件上传状态（用于批量转换页面）
  const uploadedFiles = ref<ProcessedFile[]>([])

  // 加载状态
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // ============= Getters =============

  /**
   * 获取所有文件列表
   */
  const allFiles = computed(() => {
    return files.value.allUids
      .map(uid => files.value.byUid[uid])
      .filter(Boolean)
  })

  /**
   * 根据 UID 获取文件
   */
  const getFileByUid = computed(() => {
    return (uid: string) => files.value.byUid[uid]
  })

  /**
   * 获取已上传的文件列表
   */
  const getUploadedFiles = computed(() => uploadedFiles.value)

  /**
   * 获取已上传文件的 UIDs
   */
  const getUploadedFileUids = computed(() => {
    return uploadedFiles.value
      .filter(f => f.uid)
      .map(f => f.uid!)
  })

  // ============= Actions =============

  /**
   * 上传文件
   */
  async function uploadFile(file: File): Promise<string> {
    try {
      const response = await batchAPI.uploadFile(file)

      // 更新文件存储
      const batchFile: BatchFile = {
        id: 0, // 临时ID
        uid: response.uid,
        userId: 0,
        originalName: response.originalName,
        fileSize: response.fileSize,
        fileType: response.fileType as any,
        contentHash: '',
        rawContent: '',
        totalChars: 0,
        progress: 0,
        status: response.status as BatchFileStatus,
        createdAt: response.createdAt,
        updatedAt: response.createdAt
      }

      files.value.byUid[response.uid] = batchFile
      if (!files.value.allUids.includes(response.uid)) {
        files.value.allUids.push(response.uid)
      }

      return response.uid
    } catch (err: any) {
      error.value = err.message || '上传失败'
      throw err
    }
  }

  /**
   * 批量上传文件
   */
  async function uploadFiles(fileList: File[]): Promise<string[]> {
    const uids: string[] = []

    for (const file of fileList) {
      try {
        const uid = await uploadFile(file)
        uids.push(uid)
      } catch (err) {
        console.error('上传文件失败:', file.name, err)
      }
    }

    return uids
  }

  /**
   * 获取文件信息
   */
  async function fetchFileInfo(uid: string): Promise<BatchFile> {
    try {
      const file = await batchAPI.getFileInfo(uid)

      // 更新存储
      files.value.byUid[uid] = file
      if (!files.value.allUids.includes(uid)) {
        files.value.allUids.push(uid)
      }

      return file
    } catch (err: any) {
      error.value = err.message || '获取文件信息失败'
      throw err
    }
  }

  /**
   * 获取文件内容
   */
  async function fetchFileContent(uid: string) {
    try {
      return await batchAPI.getFileContent(uid)
    } catch (err: any) {
      error.value = err.message || '获取文件内容失败'
      throw err
    }
  }

  /**
   * 删除文件
   */
  async function deleteFile(uid: string): Promise<void> {
    try {
      await batchAPI.deleteFile(uid)

      // 从存储中移除
      delete files.value.byUid[uid]
      files.value.allUids = files.value.allUids.filter(id => id !== uid)

      // 从上传列表中移除
      uploadedFiles.value = uploadedFiles.value.filter(f => f.uid !== uid)
    } catch (err: any) {
      error.value = err.message || '删除文件失败'
      throw err
    }
  }

  /**
   * 开始解析文件
   */
  async function startParse(uid: string) {
    try {
      const result = await batchAPI.startParse(uid)

      // 更新文件状态
      if (files.value.byUid[uid]) {
        files.value.byUid[uid] = {
          ...files.value.byUid[uid],
          status: result.status as BatchFileStatus,
          progress: result.progress,
          totalChars: result.totalChars || 0,
          estimateSeconds: result.estimateSeconds,
          estimateCredits: result.estimateCredits,
          encoding: result.encoding,
          baseCharsPerSec: result.baseCharsPerSec,
          warnings: result.warnings
        }
      }

      return result
    } catch (err: any) {
      error.value = err.message || '开始解析失败'
      throw err
    }
  }

  /**
   * 批量获取解析状态
   */
  async function fetchBatchParseStatus(uids: string[]) {
    try {
      const results = await batchAPI.getBatchParseStatus(uids)

      // 更新文件状态
      results.forEach(result => {
        if (files.value.byUid[result.uid]) {
          files.value.byUid[result.uid] = {
            ...files.value.byUid[result.uid],
            status: result.status as BatchFileStatus,
            progress: result.progress,
            totalChars: result.totalChars || files.value.byUid[result.uid].totalChars,
            estimateSeconds: result.estimateSeconds,
            estimateCredits: result.estimateCredits,
            encoding: result.encoding,
            baseCharsPerSec: result.baseCharsPerSec,
            warnings: result.warnings,
            errorMessage: result.errorMessage
          }
        }
      })

      return results
    } catch (err: any) {
      error.value = err.message || '获取解析状态失败'
      throw err
    }
  }

  /**
   * 创建批量任务
   * 注意：任务创建后，使用 taskStore 管理任务状态
   */
  async function createBatchTask(params: {
    uids: string[]
    title: string
    description?: string
    voice: string
    audioFormat: string
    rate: number
    pitch: number
    volume: number
    style: string
  }) {
    try {
      isLoading.value = true
      error.value = null

      const result = await batchAPI.createBatchTask(params)

      // 任务创建成功后，订阅 SSE 以接收实时状态更新
      const { useTaskStore } = await import('@/store/task')
      const taskStore = useTaskStore()
      await taskStore.subscribeGlobal()

      return result
    } catch (err: any) {
      error.value = err.message || '创建批量任务失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 批量更新或插入已处理的文件（UI层）
   * 用于文件上传与解析流程中的批量操作
   */
  function upsertUploadedFiles(files: ProcessedFile[]) {
    files.forEach(newFile => {
      const index = uploadedFiles.value.findIndex(f => f.id === newFile.id)
      if (index !== -1) {
        // 更新现有文件
        uploadedFiles.value[index] = newFile
      } else {
        // 添加新文件
        uploadedFiles.value.push(newFile)
      }
    })
  }

  /**
   * 添加已处理的文件（UI层）
   */
  function addUploadedFile(file: ProcessedFile) {
    uploadedFiles.value.push(file)
  }

  /**
   * 更新已处理的文件
   */
  function updateUploadedFile(fileId: string, updates: Partial<ProcessedFile>) {
    const index = uploadedFiles.value.findIndex(f => f.id === fileId)
    if (index !== -1) {
      uploadedFiles.value[index] = {
        ...uploadedFiles.value[index],
        ...updates
      }
    }
  }

  /**
   * 移除已处理的文件
   */
  function removeUploadedFile(fileId: string) {
    uploadedFiles.value = uploadedFiles.value.filter(f => f.id !== fileId)
  }

  /**
   * 清空已处理的文件列表
   */
  function clearUploadedFiles() {
    uploadedFiles.value = []
  }

  /**
   * 清空错误信息
   */
  function clearError() {
    error.value = null
  }

  /**
   * 重置 store
   */
  function $reset() {
    files.value = { byUid: {}, allUids: [] }
    uploadedFiles.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    // State
    files,
    uploadedFiles,
    isLoading,
    error,

    // Getters
    allFiles,
    getFileByUid,
    getUploadedFiles,
    getUploadedFileUids,

    // Actions
    uploadFile,
    uploadFiles,
    fetchFileInfo,
    fetchFileContent,
    deleteFile,
    startParse,
    fetchBatchParseStatus,
    createBatchTask,
    upsertUploadedFiles,
    addUploadedFile,
    updateUploadedFile,
    removeUploadedFile,
    clearUploadedFiles,
    clearError,
    $reset
  }
})
